/**
 * Device-only cache for Hub modules (Home, Calendar, Chat, Meetings). Data lives in
 * IndexedDB on this device — never in ATW storage — and is always refreshed from the
 * provider after it paints. Small entries are mirrored to localStorage so a screen can
 * paint synchronously on cold start, before IndexedDB opens.
 */

const DB_NAME = "atw-hub-module-cache";
const DB_VERSION = 1;
const STORE = "entries";
const MIRROR_PREFIX = "atw-hub-mc:";
const MIRROR_MAX_CHARS = 150_000;

type Entry<T = unknown> = { key: string; value: T; savedAt: number };

const memory = new Map<string, Entry>();
let dbPromise: Promise<IDBDatabase | null> | null = null;

function openDb(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    try {
      if (typeof indexedDB === "undefined") return resolve(null);
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: "key" });
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return dbPromise;
}

async function withStore<T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest | void,
): Promise<T | null> {
  const db = await openDb();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, mode);
      const req = run(tx.objectStore(STORE));
      tx.oncomplete = () => resolve(req ? ((req.result as T) ?? null) : null);
      tx.onerror = () => resolve(null);
      tx.onabort = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

function readMirror<T>(key: string): Entry<T> | null {
  try {
    const raw = localStorage.getItem(MIRROR_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Entry<T>;
    return parsed && typeof parsed.savedAt === "number" ? parsed : null;
  } catch {
    return null;
  }
}

/** Synchronous read (memory, then localStorage mirror) for first paint. */
export function peekModuleCache<T>(key: string): T | null {
  const hit = memory.get(key) as Entry<T> | undefined;
  if (hit) return hit.value;
  const mirrored = readMirror<T>(key);
  if (mirrored) {
    memory.set(key, mirrored);
    return mirrored.value;
  }
  return null;
}

export async function readModuleCache<T>(key: string): Promise<T | null> {
  const quick = peekModuleCache<T>(key);
  if (quick != null) return quick;
  const row = await withStore<Entry<T>>("readonly", (store) => store.get(key));
  if (!row) return null;
  memory.set(key, row);
  return row.value;
}

/** Milliseconds since the entry was saved, or null if it isn't cached. */
export function moduleCacheAge(key: string): number | null {
  const hit = memory.get(key) || readMirror(key);
  return hit ? Date.now() - hit.savedAt : null;
}

export async function writeModuleCache<T>(
  key: string,
  value: T,
  opts?: { mirror?: boolean },
): Promise<void> {
  const entry: Entry<T> = { key, value, savedAt: Date.now() };
  memory.set(key, entry);
  if (opts?.mirror) {
    try {
      const json = JSON.stringify(entry);
      if (json.length <= MIRROR_MAX_CHARS) localStorage.setItem(MIRROR_PREFIX + key, json);
      else localStorage.removeItem(MIRROR_PREFIX + key);
    } catch {
      /* quota — IndexedDB still has it */
    }
  }
  await withStore("readwrite", (store) => {
    store.put(entry);
  });
}

/** Keep the newest `keep` entries whose key starts with `prefix`. */
export async function pruneModuleCache(prefix: string, keep: number): Promise<void> {
  const rows = await withStore<Entry[]>("readonly", (store) => store.getAll());
  if (!rows) return;
  const stale = rows
    .filter((row) => row.key.startsWith(prefix))
    .sort((a, b) => b.savedAt - a.savedAt)
    .slice(keep);
  if (!stale.length) return;
  for (const row of stale) {
    memory.delete(row.key);
    try {
      localStorage.removeItem(MIRROR_PREFIX + row.key);
    } catch {
      /* ignore */
    }
  }
  await withStore("readwrite", (store) => {
    for (const row of stale) store.delete(row.key);
  });
}

export async function clearModuleCache(): Promise<void> {
  memory.clear();
  try {
    for (let i = localStorage.length - 1; i >= 0; i -= 1) {
      const key = localStorage.key(i);
      if (key?.startsWith(MIRROR_PREFIX)) localStorage.removeItem(key);
    }
  } catch {
    /* ignore */
  }
  await withStore("readwrite", (store) => {
    store.clear();
  });
}
